import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function PurgePostModal({ post, onClose, onPurged }) {
  const [confirm, setConfirm] = useState("");
  const [purging, setPurging] = useState(false);

  const unlocked = confirm.trim().toUpperCase() === "DELETE";

  const purge = async () => {
    if (!unlocked) return;
    setPurging(true);
    try {
      const { data } = await api.delete(`/admin/posts/${post.id}`, {
        params: { confirm: "DELETE" },
      });
      const c = data.purged || {};
      const extras = [];
      if (c.comments) extras.push(`${c.comments} comment${c.comments === 1 ? "" : "s"}`);
      if (c.poll_votes) extras.push(`${c.poll_votes} poll vote${c.poll_votes === 1 ? "" : "s"}`);
      if (c.likert_votes) extras.push(`${c.likert_votes} Likert rating${c.likert_votes === 1 ? "" : "s"}`);
      const tail = extras.length ? ` (also removed ${extras.join(", ")})` : "";
      toast.success(`Purged.${tail}`);
      onPurged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPurging(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-red-700 w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="purge-post-modal"
      >
        <div className="flex items-center justify-between border-b border-red-700 px-6 py-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-red-700">Permanent action</div>
            <h3 className="serif text-2xl mt-1">Purge post</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="purge-close"
            className="mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black"
          >
            Close
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-neutral-800 leading-relaxed">
            You are about to permanently remove &ldquo;<span className="serif italic">{post.title}</span>&rdquo; from the
            database. This will also delete any comments, votes and Likert ratings attached to it, plus any uploaded
            media file. <strong>This cannot be undone.</strong>
          </p>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              Type <span className="text-red-700">DELETE</span> to confirm
            </label>
            <input
              autoFocus
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="purge-confirm-input"
              className="mt-2 w-full border border-neutral-300 focus:border-red-700 bg-transparent px-3 py-2 focus:outline-none mono tracking-widest"
              placeholder="DELETE"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-neutral-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={purge}
            disabled={!unlocked || purging}
            data-testid="purge-confirm-button"
            className="bg-red-700 text-white px-6 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {purging ? "Purging…" : "Purge permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
